import { AlertTriangle, Info, ShieldCheck } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { limitKind } from '../../services/insightService';
import type { InsightRecommendation, TraceCorrectionPlan } from '../../services/insightService';
import './InsightChips.css';

/** Canonical element names the insight engine returns (backend icpReferenceRanges). */
const KNOWN_ELEMENTS: ReadonlySet<string> = new Set([
  'alkalinity', 'calcium', 'magnesium', 'potassium', 'strontium', 'iodine', 'nitrate', 'phosphate',
]);

/**
 * Display name of an insight element in the UI language ("Jod"). The API sends
 * a canonical English identifier; unknown elements are shown as sent.
 */
export function elementLabel(element: string, t: (key: string) => string): string {
  const key = element.trim().toLowerCase();
  return KNOWN_ELEMENTS.has(key) ? t(`insights.reef.elements.${key}`) : element;
}

export interface LimitBadgeProps {
  element: string | null;
  advisoryOnly: boolean;
}

/**
 * Enforced (alkalinity/calcium) vs advisory-only (magnesium/nitrate/phosphate)
 * marker. Always text + icon, never colour alone (WCAG 1.4.1). Elements with
 * neither an enforced limit nor the advisory flag get no marker.
 */
export function LimitBadge({ element, advisoryOnly }: LimitBadgeProps) {
  const { t } = useTranslation();
  const kind = limitKind(element, advisoryOnly);
  if (kind === 'none') return null;
  const isAdvisory = kind === 'advisory';
  const Icon = isAdvisory ? Info : ShieldCheck;
  return (
    <Badge tone={isAdvisory ? 'neutral' : 'info'} className="rg-insight-chips__limit">
      <Icon size={14} aria-hidden="true" focusable="false" />
      {isAdvisory ? t('insights.reef.advisoryLabel') : t('insights.reef.enforcedLabel')}
    </Badge>
  );
}

export interface DoseTextProps {
  amount: number | null;
  unit: string | null;
  frequency: string | null;
  unknownConcentration: boolean;
  /** Trace elements (#1829): the amount was withheld until the value is confirmed. */
  needsConfirmation?: boolean;
}

/**
 * The dose line, or an explicit notice why there is none ("confirm first" for
 * an unconfirmed trace element, else "concentration unknown") — a dose is never invented.
 */
export function DoseText({ amount, unit, frequency, unknownConcentration, needsConfirmation = false }: DoseTextProps) {
  const { t, i18n } = useTranslation();
  if (needsConfirmation) {
    return (
      <Text as="span" size="sm" className="rg-insight-chips__unknown">
        <AlertTriangle size={14} aria-hidden="true" focusable="false" />
        {t('insights.reef.needsConfirmation')}
      </Text>
    );
  }
  if (unknownConcentration || amount === null) {
    return (
      <Text as="span" size="sm" className="rg-insight-chips__unknown">
        <AlertTriangle size={14} aria-hidden="true" focusable="false" />
        {t('insights.reef.unknownConcentration')}
      </Text>
    );
  }
  return (
    <Text as="span" size="sm">
      {t('insights.reef.doseLine', {
        amount: new Intl.NumberFormat(i18n.language).format(amount),
        unit: unit ?? '',
        frequency: frequency ?? '',
      })}
    </Text>
  );
}

/** A one-time trace-element correction spread over days (#1829); nothing without a plan. */
export function CorrectionPlanText({ plan }: { plan: TraceCorrectionPlan | null | undefined }) {
  const { t, i18n } = useTranslation();
  if (!plan) return null;
  const fmt = (n: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }).format(n);
  return (
    <Text as="span" size="sm" color="secondary" data-testid="correction-plan">
      {t('insights.reef.correctionPlan', {
        count: plan.days,
        total: fmt(plan.totalRaise),
        perDay: fmt(plan.maxRaisePerDay),
        unit: plan.unit,
      })}
    </Text>
  );
}

export interface InsightChipsProps {
  recommendations: InsightRecommendation[];
  /** Opens the explanation for the recommendation at this index. */
  onExplain: (index: number) => void;
}

/** Compact list of per-element recommendations inside the Reef Intelligence card. */
export function InsightChips({ recommendations, onExplain }: InsightChipsProps) {
  const { t } = useTranslation();
  return (
    <ul className="rg-insight-chips">
      {recommendations.map((rec, index) => (
        <li
          key={`${rec.element}-${index}`}
          className={`rg-insight-chips__item rg-insight-chips__item--${limitKind(rec.element, rec.advisoryOnly)}`}
        >
          <Stack gap="2">
            <Inline justify="between" gap="2">
              <Text as="span" weight="semibold" className="rg-insight-chips__element">
                {elementLabel(rec.element, t)}
              </Text>
              <LimitBadge element={rec.element} advisoryOnly={rec.advisoryOnly} />
            </Inline>
            <Text as="span" size="sm" color="secondary">
              {rec.productType}
            </Text>
            <DoseText
              amount={rec.amount}
              unit={rec.unit}
              frequency={rec.frequency}
              unknownConcentration={rec.unknownConcentration}
              needsConfirmation={rec.needsConfirmation}
            />
            <CorrectionPlanText plan={rec.correctionPlan} />
            {rec.capped && (
              <Inline gap="2">
                <Badge tone="warning">{t('insights.reef.cappedLabel')}</Badge>
              </Inline>
            )}
            <Inline gap="2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onExplain(index)}
                aria-label={t('insights.reef.explainAriaLabel', { element: elementLabel(rec.element, t) })}
                aria-haspopup="dialog"
              >
                {t('insights.reef.explainAction')}
              </Button>
            </Inline>
          </Stack>
        </li>
      ))}
    </ul>
  );
}

export default InsightChips;
